import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Database,
  History,
  Inbox,
  LucideIcon,
  Network,
  Plus,
  Search,
  Tags,
  UserCog,
  Users,
  Waypoints,
} from 'lucide-react';
import { stakeholderService } from '../../services/stakeholderService';
import { organizationService } from '../../services/organizationService';
import { wikiService } from '../../services/wikiService';
import { workflowService } from '../../services/workflowService';
import { auditService } from '../../services/auditService';
import { AuditAction, AuditLog } from '../../types/audit';
import { PendingEntryStatus, PendingEntry } from '../../types/workflow';
import { Stakeholder } from '../../types/stakeholder';
import { Organization } from '../../types/organization';
import { AuthImage } from '../media/AuthImage';
import {
  Avatar,
  Button,
  Card,
  Dot,
  EmptyState,
  EntityIcon,
  Menu,
  Page,
  PageHeader,
  Pill,
  SectionHeader,
  Skeleton,
  StatGrid,
  StatItem,
  SemanticTone,
  actionTone,
  entityKindOf,
} from '../ui';

/* ========== types ========== */

interface DashboardStats {
  totalStakeholders: number;
  totalOrganizations: number;
  totalWikiPages: number;
  pendingEntries: number;
  recentActivity: number;
  approvalRate: number;
  approvedCount: number;
  rejectedCount: number;
}

interface WeekChange {
  pct: number;
  type: 'increase' | 'decrease';
}

/* ========== helpers ========== */

const ACTION_VERBS: Partial<Record<AuditAction, string>> = {
  [AuditAction.CREATE]: 'created',
  [AuditAction.UPDATE]: 'updated',
  [AuditAction.DELETE]: 'deleted',
  [AuditAction.APPROVE]: 'approved',
  [AuditAction.REJECT]: 'rejected',
  [AuditAction.LOGIN]: 'signed in',
  [AuditAction.LOGOUT]: 'signed out',
};

const formatEntityType = (type?: string) => {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const timeAgo = (dateStr: string) => {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatLabel = (value?: string) => {
  if (!value) return '—';
  const text = value.replace(/[-_]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const fullDate = (dateStr: string) => new Date(dateStr).toLocaleString();

/* ========== small pieces ========== */

const SeeAll: React.FC<{ to: string; label?: string }> = ({ to, label = 'See all' }) => (
  <Link
    to={to}
    className="-mr-1 inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
  >
    {label}
    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
  </Link>
);

/** Entity-kind tile; kinds without a hue (backups, sessions, …) get a neutral tile */
const KindIcon: React.FC<{ type?: string; session?: boolean }> = ({ type, session = false }) => {
  const kind = session ? 'user' : entityKindOf(type);
  if (kind) return <EntityIcon kind={kind} />;
  const icon = /(backup|restore)/i.test(type || '') ? Database : /(taxonom|categor)/i.test(type || '') ? Tags : CircleDot;
  return <EntityIcon kind="user" icon={icon} />;
};

// Verb colour follows the audit action: create green, update blue, delete red, import/export violet
const VERB_TEXT: Record<SemanticTone, string> = {
  green: 'text-emerald-600',
  blue: 'text-sky-600',
  red: 'text-primary-600',
  violet: 'text-violet-600',
  amber: 'text-amber-600',
  teal: 'text-teal-600',
  neutral: '',
  outline: '',
};

const Delta: React.FC<{ change: WeekChange }> = ({ change }) =>
  change.pct > 0 ? (
    <Pill tone={change.type === 'increase' ? 'green' : 'red'} title="vs previous 7 days">
      {change.type === 'increase' ? '+' : '−'}
      {change.pct}%
    </Pill>
  ) : null;

const RowSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <Card divided>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-2.5">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-3.5 flex-1" />
        <Skeleton className="h-3 w-8" />
      </div>
    ))}
  </Card>
);

const PageSkeleton: React.FC = () => (
  <Page>
    <div className="mb-5 flex items-end justify-between">
      <Skeleton className="h-7 w-56" />
      <Skeleton className="h-8 w-40 rounded-full" />
    </div>
    <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-neutral-200 bg-white lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-3 p-4">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-12" />
        </div>
      ))}
    </div>
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Skeleton className="mb-3 h-4 w-24" />
        <RowSkeleton rows={6} />
      </div>
      <div>
        <Skeleton className="mb-3 h-4 w-28" />
        <RowSkeleton rows={4} />
      </div>
    </div>
  </Page>
);

/* ========== rows ========== */

const ActivityRow: React.FC<{ log: AuditLog; className?: string }> = ({ log, className = '' }) => {
  const actor = log.user?.email?.split('@')[0] || 'User';
  const verb = ACTION_VERBS[log.action] ?? log.action;
  const entityName =
    log.details?.stakeholderName || log.details?.organizationName || log.details?.name || log.details?.title || '';
  const isSession = log.action === AuditAction.LOGIN || log.action === AuditAction.LOGOUT;
  const entityType = isSession ? '' : formatEntityType(log.entityType).toLowerCase();

  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${className}`}>
      <KindIcon type={log.entityType} session={isSession} />
      <p className="min-w-0 flex-1 truncate text-[13px] text-neutral-500">
        <span className="text-ink">{actor}</span> <span className={VERB_TEXT[actionTone(log.action)]}>{verb}</span>
        {entityType && ` ${entityType}`}
        {entityName && <span className="text-ink"> {entityName}</span>}
      </p>
      <time
        dateTime={log.createdAt}
        title={fullDate(log.createdAt)}
        className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400"
      >
        {timeAgo(log.createdAt)}
      </time>
    </li>
  );
};

const PendingRow: React.FC<{ entry: PendingEntry }> = ({ entry }) => {
  const entityName =
    entry.entityData?.name ||
    `${entry.entityData?.firstName || ''} ${entry.entityData?.lastName || ''}`.trim() ||
    'Unnamed';
  const submitter = entry.submitter?.email?.split('@')[0] || 'user';

  return (
    <li>
      <Link to="/pending" className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50">
        <KindIcon type={entry.entityType} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] text-ink">{entityName}</span>
          <span className="block truncate text-[12px] text-neutral-400">
            {formatEntityType(entry.entityType)} · {submitter}
          </span>
        </span>
        <time
          dateTime={entry.submittedAt}
          title={fullDate(entry.submittedAt)}
          className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400"
        >
          {timeAgo(entry.submittedAt)}
        </time>
      </Link>
    </li>
  );
};

const StakeholderRow: React.FC<{ stakeholder: Stakeholder }> = ({ stakeholder: s }) => {
  const currentRole = s.assignments?.find(a => a.isCurrent);
  const fallback = <Avatar kind="stakeholder" size="md" name={`${s.firstName || ''} ${s.lastName || ''}`} />;

  return (
    <li>
      <Link to={`/stakeholders/${s.id}`} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50">
        {s.profileImageId ? (
          <AuthImage
            mediaId={s.profileImageId}
            alt=""
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
            fallback={fallback}
          />
        ) : (
          fallback
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] text-ink">
            {s.firstName} {s.lastName}
          </span>
          <span className="block truncate text-[12px] text-neutral-400">{currentRole?.role || s.category || '—'}</span>
        </span>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
      </Link>
    </li>
  );
};

const OrganizationRow: React.FC<{ organization: Organization }> = ({ organization: org }) => (
  <li>
    <Link to={`/organizations/${org.id}`} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50">
      <Avatar kind="organization" size="md" name={org.name} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">{org.name}</span>
        <span className="block truncate text-[12px] text-neutral-400">{formatLabel(org.type)}</span>
      </span>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
    </Link>
  </li>
);

interface Shortcut {
  label: string;
  to: string;
  icon: LucideIcon;
}

/* ========== MAIN COMPONENT ========== */

export const DashboardOverview: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats>({
    totalStakeholders: 0,
    totalOrganizations: 0,
    totalWikiPages: 0,
    pendingEntries: 0,
    recentActivity: 0,
    approvalRate: 0,
    approvedCount: 0,
    rejectedCount: 0,
  });
  const [changes, setChanges] = useState<{
    stakeholders: WeekChange;
    organizations: WeekChange;
    wiki: WeekChange;
    approvalRate: WeekChange;
  }>({
    stakeholders: { pct: 0, type: 'increase' },
    organizations: { pct: 0, type: 'increase' },
    wiki: { pct: 0, type: 'increase' },
    approvalRate: { pct: 0, type: 'increase' },
  });
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [pendingList, setPendingList] = useState<PendingEntry[]>([]);
  const [recentStakeholders, setRecentStakeholders] = useState<Stakeholder[]>([]);
  const [recentOrganizations, setRecentOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role && ['super_admin', 'admin', 'top_owner'].includes(user.role);
  const isSuperAdmin = user?.role && ['super_admin', 'admin'].includes(user.role);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        // Phase 1: Core stats (parallel)
        const [stakeholdersRes, organizationsRes, wikiRes, pendingRes, approvedRes, rejectedRes, activityRes] = await Promise.all([
          stakeholderService.getStakeholders({ page: 1, limit: 5, sortBy: 'createdAt', sortOrder: 'DESC' }),
          organizationService.getOrganizations({ page: 1, limit: 5, sortBy: 'createdAt', sortOrder: 'DESC' }),
          wikiService.getWikiPages({ page: 1, limit: 1 }),
          workflowService.getPendingEntries({ status: PendingEntryStatus.PENDING, page: 1, limit: 5 }),
          workflowService.getPendingEntries({ status: PendingEntryStatus.APPROVED, page: 1, limit: 1 }),
          workflowService.getPendingEntries({ status: PendingEntryStatus.REJECTED, page: 1, limit: 1 }),
          auditService.getActivityStats(7),
        ]);

        const totalStakeholders = stakeholdersRes?.total ?? 0;
        const totalOrganizations = organizationsRes?.total ?? 0;
        const totalWikiPages = wikiRes?.total ?? 0;
        const pendingEntries = pendingRes?.total ?? 0;
        const approvedCount = approvedRes?.total ?? 0;
        const rejectedCount = rejectedRes?.total ?? 0;
        const activityStatsObj = (activityRes as any)?.stats ?? activityRes ?? {};
        const statsValues = activityStatsObj.actionsByType ?? activityStatsObj;
        const recentActivity = typeof statsValues === 'object'
          ? Object.values(statsValues).reduce((sum: number, v: any) => sum + Number(v || 0), 0)
          : 0;
        const approvalRateBase = approvedCount + rejectedCount;
        const approvalRate = approvalRateBase > 0 ? Math.round((approvedCount / approvalRateBase) * 100) : 0;

        setStats({
          totalStakeholders,
          totalOrganizations,
          totalWikiPages,
          pendingEntries,
          recentActivity,
          approvalRate,
          approvedCount,
          rejectedCount,
        });

        // Set recent items
        setRecentStakeholders((stakeholdersRes?.data ?? []) as Stakeholder[]);
        setRecentOrganizations((organizationsRes?.data ?? []) as Organization[]);
        setPendingList((pendingRes?.data ?? []) as PendingEntry[]);

        // Phase 2: Recent activity logs + week-over-week (parallel)
        const now = new Date();
        const startThisWeek = new Date(now);
        startThisWeek.setDate(now.getDate() - 7);
        const startPrevWeek = new Date(now);
        startPrevWeek.setDate(now.getDate() - 14);
        const toIso = (d: Date) => d.toISOString();

        const [logsRes, shThis, shPrev, orgThis, orgPrev, wikiThis, wikiPrev] = await Promise.all([
          auditService.getLogs({ page: 1, limit: 10 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'stakeholder', startDate: toIso(startThisWeek), endDate: toIso(now), page: 1, limit: 1 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'stakeholder', startDate: toIso(startPrevWeek), endDate: toIso(startThisWeek), page: 1, limit: 1 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'organization', startDate: toIso(startThisWeek), endDate: toIso(now), page: 1, limit: 1 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'organization', startDate: toIso(startPrevWeek), endDate: toIso(startThisWeek), page: 1, limit: 1 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'wiki_page', startDate: toIso(startThisWeek), endDate: toIso(now), page: 1, limit: 1 }),
          auditService.getLogs({ action: AuditAction.CREATE, entityType: 'wiki_page', startDate: toIso(startPrevWeek), endDate: toIso(startThisWeek), page: 1, limit: 1 }),
        ]).catch(() => [null, null, null, null, null, null, null]);

        // Recent logs (filter out read/search noise)
        const logs = (logsRes as any)?.logs ?? [];
        setRecentLogs(logs.filter((l: AuditLog) => l.action !== AuditAction.READ && l.action !== AuditAction.SEARCH));

        // Week-over-week
        const getTotalFrom = (resp: any) => resp?.pagination?.total ?? 0;
        const pct = (curr: number, prev: number) => {
          if (prev === 0) return curr > 0 ? 100 : 0;
          return Math.round(((curr - prev) / prev) * 100);
        };
        const type = (value: number) => (value >= 0 ? 'increase' : 'decrease') as 'increase' | 'decrease';

        const shPctVal = pct(getTotalFrom(shThis), getTotalFrom(shPrev));
        const orgPctVal = pct(getTotalFrom(orgThis), getTotalFrom(orgPrev));
        const wikiPctVal = pct(getTotalFrom(wikiThis), getTotalFrom(wikiPrev));

        setChanges({
          stakeholders: { pct: Math.abs(shPctVal), type: type(shPctVal) },
          organizations: { pct: Math.abs(orgPctVal), type: type(orgPctVal) },
          wiki: { pct: Math.abs(wikiPctVal), type: type(wikiPctVal) },
          approvalRate: { pct: 0, type: 'increase' },
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const displayName = user?.firstName || user?.email?.split('@')[0] || 'there';

  if (isLoading) {
    return <PageSkeleton />;
  }

  const statItems: StatItem[] = [
    {
      label: 'Stakeholders',
      value: stats.totalStakeholders.toLocaleString(),
      badge: <Delta change={changes.stakeholders} />,
      to: '/stakeholders',
      icon: <EntityIcon kind="stakeholder" size="xs" />,
    },
    {
      label: 'Organizations',
      value: stats.totalOrganizations.toLocaleString(),
      badge: <Delta change={changes.organizations} />,
      to: '/organizations',
      icon: <EntityIcon kind="organization" size="xs" />,
    },
    {
      label: 'Wiki pages',
      value: stats.totalWikiPages.toLocaleString(),
      badge: <Delta change={changes.wiki} />,
      to: '/wiki',
      icon: <EntityIcon kind="wiki" size="xs" />,
    },
    isAdmin
      ? {
          label: 'Pending review',
          value: stats.pendingEntries.toLocaleString(),
          to: '/pending',
          // Amber only while something is waiting
          icon: (
            <span
              aria-hidden="true"
              className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md ${
                stats.pendingEntries > 0 ? 'bg-amber-50 text-amber-700' : 'bg-neutral-100 text-neutral-500'
              }`}
            >
              <Inbox className="h-3 w-3" strokeWidth={1.75} />
            </span>
          ),
        }
      : {
          label: 'Activity · 7d',
          value: stats.recentActivity.toLocaleString(),
          icon: <EntityIcon kind="user" icon={History} size="xs" />,
        },
  ];

  const shortcuts: Shortcut[] = [
    { label: 'Relationships', to: '/relationships', icon: Waypoints },
    { label: 'Influence', to: '/influence-graph', icon: Network },
    { label: 'Search', to: '/search', icon: Search },
    ...(isSuperAdmin
      ? [
          { label: 'Users', to: '/users', icon: UserCog },
          { label: 'Categories', to: '/taxonomies', icon: Tags },
          { label: 'Audit', to: '/audit', icon: History },
          { label: 'Data', to: '/admin', icon: Database },
        ]
      : []),
    ...(isAdmin && !isSuperAdmin ? [{ label: 'Pending', to: '/pending', icon: Inbox }] : []),
  ];

  const reviewed = stats.approvedCount + stats.rejectedCount;

  return (
    <Page>
      <PageHeader
        title={<span data-testid="dashboard-header">{greeting}, {displayName}</span>}
        actions={
          <>
            <Menu
              items={[
                { label: 'Organization', icon: Building2, onSelect: () => navigate('/organizations/new') },
                { label: 'Wiki page', icon: BookOpen, onSelect: () => navigate('/wiki/new') },
              ]}
              trigger={props => (
                <Button size="sm" variant="secondary" {...props}>
                  New
                  <ChevronDown className="-mr-0.5 h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
                </Button>
              )}
            />
            <Button size="sm" variant="primary" onClick={() => navigate('/stakeholders/new')}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              Stakeholder
            </Button>
          </>
        }
      />

      <div data-testid="dashboard-stats">
        <StatGrid items={statItems} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ---- Main column ---- */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <section>
            <SectionHeader title="Activity" aside={isSuperAdmin ? <SeeAll to="/audit" /> : undefined} />
            <Card>
              {recentLogs.length > 0 ? (
                <ul className="divide-y divide-neutral-200/70">
                  {recentLogs.slice(0, 8).map((log, index) => (
                    <ActivityRow key={log.id} log={log} className={index >= 5 ? 'max-sm:hidden' : ''} />
                  ))}
                </ul>
              ) : (
                <EmptyState icon={History} title="No activity yet" />
              )}
            </Card>
          </section>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-4">
            <section className="min-w-0">
              <SectionHeader title="New stakeholders" aside={<SeeAll to="/stakeholders" />} />
              <Card>
                {recentStakeholders.length > 0 ? (
                  <ul className="divide-y divide-neutral-200/70">
                    {recentStakeholders.slice(0, 4).map(s => (
                      <StakeholderRow key={s.id} stakeholder={s} />
                    ))}
                  </ul>
                ) : (
                  <EmptyState icon={Users} title="No stakeholders yet" />
                )}
              </Card>
            </section>

            <section className="min-w-0">
              <SectionHeader title="New organizations" aside={<SeeAll to="/organizations" />} />
              <Card>
                {recentOrganizations.length > 0 ? (
                  <ul className="divide-y divide-neutral-200/70">
                    {recentOrganizations.slice(0, 4).map(org => (
                      <OrganizationRow key={org.id} organization={org} />
                    ))}
                  </ul>
                ) : (
                  <EmptyState icon={Building2} title="No organizations yet" />
                )}
              </Card>
            </section>
          </div>
        </div>

        {/* ---- Side column ---- */}
        <div className="min-w-0 space-y-6">
          {isAdmin && (
            <section>
              <SectionHeader
                title={
                  <span className="inline-flex items-center gap-2">
                    Pending review
                    {stats.pendingEntries > 0 && <Pill tone="amber">{stats.pendingEntries}</Pill>}
                  </span>
                }
                aside={stats.pendingEntries > 0 ? <SeeAll to="/pending" /> : undefined}
              />
              <Card>
                {pendingList.length > 0 ? (
                  <ul className="divide-y divide-neutral-200/70">
                    {pendingList.slice(0, 5).map(entry => (
                      <PendingRow key={entry.id} entry={entry} />
                    ))}
                    {stats.pendingEntries > 5 && (
                      <li>
                        <Link
                          to="/pending"
                          className="block px-4 py-2.5 text-[13px] text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-ink"
                        >
                          {stats.pendingEntries - 5} more
                        </Link>
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="flex items-center gap-2.5 px-4 py-3 text-[13px] text-neutral-500">
                    <CheckCheck className="h-4 w-4 text-emerald-500" strokeWidth={1.75} />
                    All caught up
                  </p>
                )}
              </Card>
            </section>
          )}

          {isSuperAdmin && (
            <section>
              <SectionHeader title="Review" />
              <Card divided>
                <div className="px-4 py-3">
                  <div className="flex items-baseline justify-between text-[13px]">
                    <span className="text-neutral-500">Approval rate</span>
                    <span className="text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">
                      {reviewed > 0 ? `${stats.approvalRate}%` : '—'}
                    </span>
                  </div>
                  <div
                    className="mt-3 h-1 overflow-hidden rounded-full bg-neutral-100"
                    role="progressbar"
                    aria-label="Approval rate"
                    aria-valuenow={stats.approvalRate}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${stats.approvalRate}%` }} />
                  </div>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-[13px]">
                  <span className="inline-flex items-center gap-2 text-neutral-500">
                    <Dot tone="green" />
                    Approved
                  </span>
                  <span className="tabular-nums">{stats.approvedCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-[13px]">
                  <span className="inline-flex items-center gap-2 text-neutral-500">
                    <Dot tone="red" />
                    Rejected
                  </span>
                  <span className="tabular-nums">{stats.rejectedCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-[13px]">
                  <span className="text-neutral-500">Actions · 7d</span>
                  <span className="tabular-nums">{stats.recentActivity.toLocaleString()}</span>
                </div>
              </Card>
            </section>
          )}

          <section>
            <SectionHeader title="Shortcuts" />
            <nav
              aria-label="Shortcuts"
              className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-200/70"
            >
              {shortcuts.map(({ label, to, icon: Icon }, index) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex h-11 items-center gap-2.5 bg-white px-4 text-[13px] text-ink transition-colors hover:bg-neutral-50 ${
                    index === shortcuts.length - 1 && shortcuts.length % 2 === 1 ? 'col-span-2' : ''
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                  <span className="truncate">{label}</span>
                </Link>
              ))}
            </nav>
          </section>
        </div>
      </div>
    </Page>
  );
};
